import type { SizingExperimentResponseDto } from "@babackapp/private-api-client/types";
import useActivateSizingExperiment from "@babackapp/private-api-client/client/use-activate-sizing-experiment";
import useDeactivateSizingExperiment from "@babackapp/private-api-client/client/use-deactivate-sizing-experiment";
import { Badge } from "@babackapp/ui/components/badge";
import { Button } from "@babackapp/ui/components/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@babackapp/ui/components/table";
import { toast } from "@babackapp/ui/components/sonner";
import { format } from "date-fns";
import { Play, Square, Pencil, BarChart3 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { EditExperimentDialog } from "./edit-experiment-dialog";

interface ExperimentsTableProps {
  experiments: SizingExperimentResponseDto[];
  shopId: string;
}

export function ExperimentsTable({
  experiments,
  shopId,
}: ExperimentsTableProps) {
  const navigate = useNavigate();
  const activate = useActivateSizingExperiment(shopId);
  const deactivate = useDeactivateSizingExperiment(shopId);
  const [editingExperiment, setEditingExperiment] =
    useState<SizingExperimentResponseDto | null>(null);

  const sorted = [...experiments].sort((a, b) => {
    if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
    return b.createdAt - a.createdAt;
  });

  function handleActivate(experimentId: string) {
    activate.mutate(
      { experimentId },
      {
        onError: () => {
          toast.error(
            "Cannot activate: another experiment is already active. Deactivate it first.",
          );
        },
      },
    );
  }

  function handleDeactivate(experimentId: string) {
    deactivate.mutate({ experimentId });
  }

  if (sorted.length === 0) {
    return (
      <div className="rounded-md border bg-white p-8 text-center text-sm text-muted-foreground">
        No experiments yet. Create one to start A/B testing the sizing widget.
      </div>
    );
  }

  return (
    <>
      <div className="rounded-md bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Split %</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Started</TableHead>
              <TableHead>Ended</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((exp) => (
              <TableRow
                key={exp.experimentId}
                className={exp.isActive ? "bg-green-50/50" : ""}
              >
                <TableCell
                  className="font-medium cursor-pointer hover:underline"
                  onClick={() => navigate(`${exp.experimentId}`)}
                >
                  {exp.name}
                </TableCell>
                <TableCell>{exp.splitPercent}%</TableCell>
                <TableCell>
                  <Badge variant={exp.isActive ? "default" : "secondary"}>
                    {exp.isActive ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {exp.startedAt
                    ? format(new Date(exp.startedAt), "dd/MM/yyyy HH:mm")
                    : "—"}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {exp.endedAt
                    ? format(new Date(exp.endedAt), "dd/MM/yyyy HH:mm")
                    : "—"}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {format(new Date(exp.createdAt), "dd/MM/yyyy")}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() =>
                        navigate(`${exp.experimentId}`)
                      }
                    >
                      <BarChart3 className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setEditingExperiment(exp)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    {exp.isActive ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-600 hover:text-red-700"
                        onClick={() => handleDeactivate(exp.experimentId)}
                        disabled={deactivate.isPending}
                      >
                        <Square className="h-3.5 w-3.5" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-green-600 hover:text-green-700"
                        onClick={() => handleActivate(exp.experimentId)}
                        disabled={activate.isPending}
                      >
                        <Play className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <EditExperimentDialog
        experiment={editingExperiment}
        shopId={shopId}
        onClose={() => setEditingExperiment(null)}
      />
    </>
  );
}
